/**
 * Shared motion vocabulary for the onboarding chat.
 *
 * Everything animated in this flow pulls from here so the whole experience
 * reads as one system: the same spring, the same rise-and-unblur entrance, the
 * same stagger rhythm. Components should reach for a variant rather than
 * hand-rolling a transition.
 */
import type { Transition, Variants } from "framer-motion";

/** The flow's signature spring: quick and critically damped, so nothing overshoots. */
export const SPRING: Transition = {
  type: "spring",
  stiffness: 420,
  damping: 38,
  mass: 0.8,
};

/** Softer spring for larger surfaces (cards, panels) that shouldn't bounce. */
export const SOFT_SPRING: Transition = {
  type: "spring",
  stiffness: 260,
  damping: 30,
};

/** Chat bubbles rise and come into focus, as if surfacing. */
export const bubbleVariants: Variants = {
  hidden: { opacity: 0, y: 14, filter: "blur(6px)", scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    scale: 1,
    transition: SPRING,
  },
  exit: {
    opacity: 0,
    y: -8,
    filter: "blur(4px)",
    transition: { duration: 0.15 },
  },
};

/** Step widgets settle in just behind the message that introduces them. */
export const widgetVariants: Variants = {
  hidden: { opacity: 0, y: 18, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { ...SOFT_SPRING, delay: 0.08 },
  },
  exit: { opacity: 0, y: -10, scale: 0.98, transition: { duration: 0.18 } },
};

/**
 * Container for lists that should reveal one item at a time — thesis criteria
 * chips, pipeline stages, suggestion rows.
 */
export const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.045, delayChildren: 0.06 },
  },
};

/** Chips pop rather than slide — it reads as "the AI found this". */
export const chipVariants: Variants = {
  hidden: { opacity: 0, scale: 0.6, y: 8 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", stiffness: 520, damping: 24 },
  },
  exit: {
    opacity: 0,
    scale: 0.7,
    transition: { duration: 0.14 },
  },
};

/** Generic list row: slides in from the left edge of its container. */
export const rowVariants: Variants = {
  hidden: { opacity: 0, x: -12 },
  visible: { opacity: 1, x: 0, transition: SPRING },
  exit: { opacity: 0, x: 12, transition: { duration: 0.16 } },
};

/**
 * Strips motion for users who've asked their OS to reduce it. Framer's
 * `useReducedMotion` covers transforms, but variants that animate `filter` or
 * `scale` still read as movement — so we swap in a plain cross-fade.
 */
export function reduce(variants: Variants): Variants {
  return {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.12 } },
    exit: { opacity: 0, transition: { duration: 0.1 } },
    ...(variants.custom ? { custom: variants.custom } : {}),
  };
}

/** Picks the real variants or their reduced-motion equivalent. */
export function motionSafe(variants: Variants, shouldReduce: boolean | null) {
  return shouldReduce ? reduce(variants) : variants;
}
